// Path: src/components/workshoppage/WorkspacePanel.tsx
import React, { useEffect, useState, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import {
  Monitor, Code, Upload, File as FileIcon, Copy, Check, Loader2,
  Maximize2, Minimize2, ChevronLeft, ChevronRight, Trash2, BookOpen, FileText, CheckCircle2, Video, Cloud, Brain,
  Target, User, Users, Link, Image as ImageIcon
} from 'lucide-react';
import { ScaleContainer } from './ScaleContainer';
import { type TabType } from '../../types/workshop';
import { useAuth } from '../../contexts/AuthContext';

// 引入类型，如果启用了 verbatimModuleSyntax 请使用 import type
import type { SlideDetail, CloudResource, MemorySnapshot } from '../../lib/api';

// 引入我们刚刚抽离的超强子组件！
import { VideoGeneratorTab } from './VideoGeneratorTab';

interface Props {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  currentHtml: string;
  setCurrentHtml: (html: string) => void;
  currentSideGuide: string;
  setCurrentSideGuide: (guide: string) => void;
  currentSideGuideContent: string;
  setCurrentSideGuideContent: (content: string) => void;
  currentMemory: string;
  setCurrentMemory: (memory: string) => void;
  slide: SlideDetail | null;
  slideId: number;
  onFileUpload: (file: File) => Promise<any>;
  resources: CloudResource[];
  isResourcesLoading?: boolean;
  onNavigation: (dir: 'prev' | 'next') => void;
  isFullscreen: boolean;
  onToggleFullscreen: (state: boolean) => void;
  onDeleteResource: (resourceId: number) => Promise<void>;
  onSave?: () => void;
  auditContent?: string;
}

export const WorkspacePanel = ({
  activeTab, setActiveTab, currentHtml, setCurrentHtml, currentSideGuide, setCurrentSideGuide, currentSideGuideContent, setCurrentSideGuideContent, currentMemory, setCurrentMemory, slide, slideId, onFileUpload,
  resources, isResourcesLoading = false, onNavigation,
  isFullscreen, onToggleFullscreen, onDeleteResource, onSave, auditContent
}: Props) => {
  const { user, token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  // 解析 currentMemory 为结构化记忆快照对象（兼容非结构化纯文本回退）
  const memorySnapshot = useMemo<MemorySnapshot | null>(() => {
    if (!currentMemory) return null;
    try {
      const parsed = JSON.parse(currentMemory);
      if (parsed && typeof parsed === 'object' && ('main_goal' in parsed || 'microcheck' in parsed)) {
        return parsed as MemorySnapshot;
      }
    } catch {
      // 解析失败，回退到纯文本展示
    }
    return null;
  }, [currentMemory]);

  const constructIframeContent = (htmlCode: string) => {
    const userInfo = JSON.stringify(user || null);
    const bridgeScript = `
      <script>
        window.parentUser = ${userInfo};
        window.parentToken = ${token ? JSON.stringify(token) : 'null'};
        window.requestNavigation = function(direction) {
          try { window.parent.postMessage({ type: 'NEXUS_NAVIGATE', direction: direction }, '*'); } 
          catch (e) { console.error(e); }
        };
        window.nextPage = function() { window.requestNavigation('next'); };
        window.prevPage = function() { window.requestNavigation('prev'); };
        window.getUserInfo = function() {
          return window.parentUser;
        };
        window.isLoggedIn = function() {
          return window.parentUser !== null;
        };
      </script>
    `;
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>body, html { margin: 0; padding: 0; width: 100%; height: 100%; overflow: hidden; background-color: white; }</style>
          ${bridgeScript}
        </head>
        <body>
          ${htmlCode}
        </body>
      </html>
    `;
  };

  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onNavigation('prev');
      if (e.key === 'ArrowRight') onNavigation('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, onNavigation]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        const editableTabs = ['code', 'guide', 'lesson', 'memory'];
        if (editableTabs.includes(activeTab) && onSave) {
          e.preventDefault();
          onSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, onSave]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];

    setUploading(true);
    try {
      await onFileUpload(file);
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleDeleteClick = async (id: number, name: string) => {
    if (!window.confirm(`确定要删除文件 "${name}" 吗？\n此操作将从云端永久删除文件。`)) return;
    try {
      await onDeleteResource(id);
    } catch (e) {
      console.error("Delete failed in panel", e);
    }
  };

  // 全屏视图渲染组件
  const FullscreenView = () => (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center animate-in fade-in duration-300">
      <div className="w-full h-full flex items-center justify-center p-0">
        <ScaleContainer>
          <iframe
            srcDoc={constructIframeContent(currentHtml)}
            title="Slide Fullscreen Preview"
            className="w-full h-full border-0 bg-white shadow-none"
            sandbox="allow-scripts allow-popups allow-same-origin allow-forms allow-modals"
          />
        </ScaleContainer>
      </div>

      <button
        onClick={() => onToggleFullscreen(false)}
        className="absolute top-6 right-6 bg-black/50 hover:bg-red-600/90 text-white/80 hover:text-white p-3 rounded-full backdrop-blur-md transition-all group z-50 shadow-lg border border-white/10"
        title="退出全屏 (Exit Fullscreen)"
      >
        <Minimize2 size={24} className="group-hover:scale-90 transition-transform" />
      </button>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-6 bg-black/60 backdrop-blur-xl text-white px-8 py-4 rounded-full border border-white/10 shadow-2xl transition-all hover:bg-black/80 hover:border-white/20 select-none">
        <button onClick={() => onNavigation('prev')} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-95">
          <ChevronLeft size={28} />
        </button>
        <div className="flex flex-col items-center min-w-[60px]">
          <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">PAGE</span>
          <span className="font-mono font-bold text-2xl leading-none tracking-tight">{slide?.slide_index || '?'}</span>
        </div>
        <button onClick={() => onNavigation('next')} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-95">
          <ChevronRight size={28} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-100">
      {isFullscreen && <FullscreenView />}

      {/* Top Tabs Navigation */}
      <div className="bg-white border-b px-4 flex justify-between items-center h-12">
        <div className="flex space-x-1">
          {[
            { id: 'preview', icon: Monitor, label: '预览' },
            { id: 'code', icon: Code, label: '代码' },
            { id: 'guide', icon: BookOpen, label: '设计' },
            { id: 'video', icon: Video, label: '视频' },
            { id: 'lesson', icon: FileText, label: '教案' },
            { id: 'resources', icon: Cloud, label: '云库' },
            { id: 'audit', icon: CheckCircle2, label: '审核' },
            { id: 'memory', icon: Brain, label: '记忆' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 px-4 py-1.5 text-sm rounded-md transition-all border ${
                activeTab === tab.id
                  ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative">

        {/* TAB 1: Preview */}
        <div className={`w-full h-full flex flex-col p-6 ${activeTab === 'preview' ? 'block' : 'hidden'}`}>
          <div className="flex-1 flex items-center justify-center relative bg-gray-200/50 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden group">
            <ScaleContainer>
              <iframe
                srcDoc={constructIframeContent(currentHtml)}
                title="Slide Preview"
                className="w-full h-full border-0 bg-white shadow-2xl"
                sandbox="allow-scripts allow-popups allow-same-origin allow-forms allow-modals"
              />
            </ScaleContainer>
            <button
              onClick={() => onToggleFullscreen(true)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-blue-600 hover:text-white text-gray-600 p-2.5 rounded-lg shadow-md transition-all opacity-0 group-hover:opacity-100 z-10 scale-90 group-hover:scale-100"
            >
              <Maximize2 size={20} />
            </button>
          </div>
        </div>

        {/* TAB 2: Code Editor */}
        <div className={`w-full h-full ${activeTab === 'code' ? 'block' : 'hidden'}`}>
          <Editor
            height="100%"
            defaultLanguage="html"
            value={currentHtml}
            onChange={(val: string | undefined) => setCurrentHtml(val || '')}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              padding: { top: 20 },
              wordWrap: 'on',
              scrollBeyondLastLine: false
            }}
          />
        </div>

        {/* TAB 3: 设计思路 */}
        <div className={`w-full h-full p-8 overflow-y-auto bg-white ${activeTab === 'guide' ? 'block' : 'hidden'}`}>
          <div className="mx-auto prose prose-blue max-w-none">
            <h2>📌 设计思路</h2>
            <div className="mt-4">
              <Editor
                height="400px"
                defaultLanguage="markdown"
                value={currentSideGuideContent}
                onChange={(val: string | undefined) => setCurrentSideGuideContent(val || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  padding: { top: 20 },
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  placeholder: "请输入设计思路内容..."
                }}
              />
            </div>

            <hr className="my-8 border-gray-200" />

            <h3>💡 开发提示</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 text-sm">
              <li>请确保交互逻辑简单直观，适合学生操作。</li>
              <li>所有样式建议使用 Tailwind CSS CDN 或内联样式。</li>
              <li>避免使用外部不可靠的 CDN 链接，请优先使用 OSS 上传功能。</li>
              <li>脚本中禁止包含恶意跳转或弹窗代码。</li>
            </ul>
          </div>
        </div>

        {/* TAB 4: 生成视频 (✨ Vibe Coding Refactored Component) */}
        <VideoGeneratorTab 
          slideId={slideId} 
          isActive={activeTab === 'video'} 
        />

        {/* TAB 5: 每页教案 */}
        <div className={`w-full h-full p-8 overflow-y-auto bg-white ${activeTab === 'lesson' ? 'block' : 'hidden'}`}>
          <div className="mx-auto prose prose-blue max-w-none">
            <h2>📚 每页教案</h2>
            <div className="mt-4">
              <Editor
                height="600px"
                defaultLanguage="markdown"
                value={currentSideGuide}
                onChange={(val: string | undefined) => setCurrentSideGuide(val || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  padding: { top: 20 },
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  placeholder: "请输入每页教案内容..."
                }}
              />
            </div>

            <hr className="my-8 border-gray-200" />

            <h3>📝 教案编写指南</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 text-sm">
              <li>教案应包含教学目标、教学重难点、教学过程等内容。</li>
              <li>每个教学环节应明确时间分配和教学方法。</li>
              <li>教案内容应与幻灯片内容保持一致。</li>
              <li>建议使用结构化的格式编写教案，便于教师参考。</li>
            </ul>
          </div>
        </div>

        {/* TAB 6: 云资源 */}
        <div className={`w-full h-full flex flex-col p-6 bg-white ${activeTab === 'resources' ? 'block' : 'hidden'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Cloud size={20} className="text-blue-500" />
              关联云资源 (OSS)
            </h2>
            <label className={`cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? '上传中...' : '上传文件'}
              <input type="file" className="hidden" onChange={handleFileChange} disabled={uploading} />
            </label>
          </div>

          {isResourcesLoading ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <Loader2 size={24} className="animate-spin mr-2" /> 加载资源中...
            </div>
          ) : resources.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <Cloud size={48} className="mb-4 opacity-50" />
              <p>暂无关联资源</p>
              <p className="text-sm mt-2">点击右上角按钮上传图片或视频</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {(() => {
                const grouped = resources.reduce((acc, file) => {
                  const key = file.artifact_id;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(file);
                  return acc;
                }, {} as Record<number, CloudResource[]>);
                
                const sortedGroups = Object.entries(grouped)
                  .map(([artifactId, files]) => ({
                    artifactId: parseInt(artifactId),
                    files
                  }))
                  .sort((a, b) => b.artifactId - a.artifactId);

                return sortedGroups.map(({ artifactId, files }) => (
                  <div key={artifactId} className="mb-6">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        幻灯片 #{artifactId}
                      </span>
                      <span className="text-xs text-gray-400">
                        {files.length} 个资源
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {files.map((file) => {
                        const isImage = file.file_type?.startsWith('image/') || 
                          /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(file.file_name);
                        const isVideo = file.file_type?.startsWith('video/') || 
                          /\.(mp4|webm|ogg|mov)$/i.test(file.file_name);
                        
                        const handleContextMenu = (e: React.MouseEvent) => {
                          e.preventDefault();
                          handleCopy(file.public_url);
                        };

                        const handleOpenPreview = () => {
                          window.open(file.public_url, '_blank');
                        };

                        return (
                          <div 
                            key={file.id} 
                            className="group relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                            onContextMenu={handleContextMenu}
                            onClick={handleOpenPreview}
                            title="左键点击在新窗口打开，右键复制链接"
                          >
                            <div className="aspect-square flex items-center justify-center bg-gray-100 overflow-hidden">
                              {isImage ? (
                                <img 
                                  src={file.public_url} 
                                  alt={file.file_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : isVideo ? (
                                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                  <video 
                                    src={file.public_url}
                                    className="max-w-full max-h-full object-contain"
                                    muted
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                                      <svg className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z"/>
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                  <FileIcon size={32} />
                                  <span className="text-xs mt-2 px-2 text-center truncate w-full">
                                    {file.file_name.split('.').pop()?.toUpperCase() || 'FILE'}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <div className="p-2 border-t border-gray-100">
                              <p className="text-xs text-gray-600 truncate" title={file.file_name}>
                                {file.file_name}
                              </p>
                            </div>

                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopy(file.public_url);
                                }}
                                className={`p-1.5 rounded-md shadow-md transition-all ${
                                  copiedUrl === file.public_url
                                    ? 'bg-green-500 text-white'
                                    : 'bg-white text-gray-600 hover:bg-blue-500 hover:text-white'
                                }`}
                                title="复制链接"
                              >
                                {copiedUrl === file.public_url ? <Check size={14} /> : <Copy size={14} />}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(file.id, file.file_name);
                                }}
                                className="p-1.5 rounded-md bg-white text-gray-600 hover:bg-red-500 hover:text-white shadow-md transition-all"
                                title="删除文件"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>

                            {copiedUrl === file.public_url && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/50 pointer-events-none">
                                <div className="bg-white px-3 py-1.5 rounded-md text-sm text-green-600 font-medium flex items-center gap-1">
                                  <Check size={16} /> 已复制链接
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              💡 提示：左键点击资源可在新窗口打开预览，右键点击可复制链接地址
            </p>
          </div>
        </div>

        {/* TAB 7: 审核 */}
        <div className={`w-full h-full p-8 overflow-y-auto bg-white ${activeTab === 'audit' ? 'block' : 'hidden'}`}>
          <div className="mx-auto prose prose-blue">
            <h2>✅ 审核信息</h2>
            <div className="bg-purple-50 p-4 border-l-4 border-purple-400 text-gray-700 rounded-r-lg whitespace-pre-wrap text-sm leading-relaxed">
              {auditContent || slide?.audit || "暂无审核信息。点击左侧\"审核课件\"按钮进行AI自动审核。"}
            </div>

            <hr className="my-8 border-gray-200" />

            <h3>📋 审核标准</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 text-sm">
              <li>内容是否符合教学大纲要求。</li>
              <li>是否存在错误或不准确的信息。</li>
              <li>设计是否符合学生认知规律。</li>
              <li>是否存在不适宜的内容。</li>
            </ul>
          </div>
        </div>

        {/* TAB 8: 记忆 */}
        <div className={`w-full h-full p-8 overflow-y-auto bg-white ${activeTab === 'memory' ? 'block' : 'hidden'}`}>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-xl font-bold mb-2">🧠 单页教研记忆快照</h2>
            <p className="text-gray-500 text-sm mb-6">AI 教研生成的单页记忆快照，生成教案后自动填充并保存到服务端。</p>

            {memorySnapshot ? (
              <div className="space-y-4">
                {/* 基本信息 */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText size={16} /> 基本信息
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">页码：</span>{memorySnapshot.page}</div>
                    <div><span className="text-gray-500">环节：</span>{memorySnapshot.segment}</div>
                    <div><span className="text-gray-500">页面模式：</span>{memorySnapshot.page_mode}</div>
                    <div><span className="text-gray-500">主题模式：</span>{memorySnapshot.theme_mode}</div>
                    <div><span className="text-gray-500">工具状态：</span>{memorySnapshot.tool_state}</div>
                  </div>
                </div>

                {/* 教学目标 */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                    <Target size={16} /> 教学目标
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{memorySnapshot.main_goal}</p>
                </div>

                {/* 教师信号 */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h3 className="font-semibold text-purple-700 mb-2 flex items-center gap-2">
                    <User size={16} /> 教师信号
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{memorySnapshot.teacher_signal}</p>
                </div>

                {/* 学生首动作 */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <Users size={16} /> 学生首动作
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{memorySnapshot.student_first_action}</p>
                </div>

                {/* 微检测 */}
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <h3 className="font-semibold text-orange-700 mb-2 flex items-center gap-2">
                    <CheckCircle2 size={16} /> 微检测
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{memorySnapshot.microcheck}</p>
                </div>

                {/* 交接钩子 */}
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                  <h3 className="font-semibold text-cyan-700 mb-2 flex items-center gap-2">
                    <Link size={16} /> 交接钩子
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{memorySnapshot.handoff_hook}</p>
                </div>

                {/* 媒体决策 */}
                <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                  <h3 className="font-semibold text-pink-700 mb-2 flex items-center gap-2">
                    <ImageIcon size={16} /> 媒体决策
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{memorySnapshot.media_decision}</p>
                </div>
              </div>
            ) : currentMemory ? (
              /* 回退：非结构化纯文本（兼容旧数据/用户手动编辑） */
              <div className="mt-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-700">
                  ⚠️ 当前记忆为非结构化文本（非 AI 自动生成的快照格式）
                </div>
                <pre className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap break-words">{currentMemory}</pre>
              </div>
            ) : (
              /* 空状态 */
              <div className="text-center py-12 text-gray-400">
                <Brain size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-base">暂无记忆快照</p>
                <p className="text-xs mt-2">AI 生成页边教案后将自动填充</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};